import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';

/**
 * Welcome promo modal — shows once per browser tab session, copies code, optionally applies cart discount.
 * @extends {DialogComponent}
 */
class WelcomePromo extends DialogComponent {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #openTimer = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    this.#clearLegacyLocalStorage();

    if (this.#shouldSkipAutoOpen()) return;

    const delay = Number(this.dataset.delay || 1800);
    this.#openTimer = setTimeout(() => {
      this.showDialog();
    }, Number.isFinite(delay) ? Math.max(0, delay) : 1800);
  }

  /**
   * Open modal without focusing the Copy button (avoids autofocus outline on load).
   */
  showDialog() {
    this.#persistShown();
    super.showDialog();

    // Parent opens in rAF; wait until after that so we can move focus off the first button.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const dialog = this.refs.dialog;
        if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

        if (!dialog.hasAttribute('tabindex')) {
          dialog.setAttribute('tabindex', '-1');
        }

        dialog.focus({ preventScroll: true });
      });
    });
  }

  disconnectedCallback() {
    if (this.#openTimer) clearTimeout(this.#openTimer);
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#activeFetch?.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    super.disconnectedCallback();
  }

  /**
   * Close + remember for this browsing session.
   * @param {Event} [event]
   */
  dismiss = (event) => {
    event?.preventDefault?.();
    this.#persistShown();
    this.closeDialog();
  };

  /**
   * Copy promo code, optionally apply to cart, then update button feedback.
   * @param {Event} event
   */
  copyAndApply = async (event) => {
    event?.preventDefault?.();

    const code = (this.dataset.promoCode || '').trim();
    if (!code) return;

    const button = this.refs.copyButton;
    const copiedLabel = this.dataset.copiedLabel || 'Copied!';
    const appliedLabel = this.dataset.appliedLabel || 'Applied!';

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      this.#fallbackCopy(code);
    }

    this.#setButtonLabel(button, copiedLabel);
    this.#announce(copiedLabel);

    let applied = false;
    if (this.dataset.autoApply !== 'false') {
      applied = await this.#applyDiscount(code);
    }

    if (applied) {
      this.#setButtonLabel(button, appliedLabel);
      this.#announce(appliedLabel);
    }

    this.#persistShown();

    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.closeDialog();
    }, 1100);
  };

  #onDialogClose = () => {
    this.#persistShown();
  };

  #shouldSkipAutoOpen() {
    if (this.hasAttribute('data-force-open')) return false;
    if (document.documentElement.classList.contains('shopify-design-mode')) return false;

    try {
      return sessionStorage.getItem(this.#storageKey()) === '1';
    } catch {
      return false;
    }
  }

  /** Mark as shown for this tab session (survives refresh + in-site navigation). */
  #persistShown() {
    if (document.documentElement.classList.contains('shopify-design-mode')) return;
    if (this.hasAttribute('data-force-open')) return;

    try {
      sessionStorage.setItem(this.#storageKey(), '1');
    } catch {
      // ignore quota / private mode
    }
  }

  /** Drop old multi-day localStorage keys from the previous behavior. */
  #clearLegacyLocalStorage() {
    try {
      localStorage.removeItem(this.#storageKey());
    } catch {
      // ignore
    }
  }

  #storageKey() {
    return this.dataset.storageKey || 'welcome-promo-session';
  }

  /**
   * @param {string} code
   * @returns {Promise<boolean>}
   */
  async #applyDiscount(code) {
    if (!window.Theme?.routes?.cart_update_url) return false;

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const existing = await this.#existingDiscountCodes(abortController.signal);
      if (existing.map((c) => c.toUpperCase()).includes(code.toUpperCase())) {
        return true;
      }

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existing, code].join(','),
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) return false;

      const data = await response.json();
      const match = data?.discount_codes?.find(
        (/** @type {{ code: string; applicable: boolean }} */ d) =>
          d.code?.toUpperCase() === code.toUpperCase()
      );

      return Boolean(match?.applicable);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return false;
      return false;
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<string[]>}
   */
  async #existingDiscountCodes(signal) {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { signal });
      if (!response.ok) return [];
      const cart = await response.json();
      return (cart.discount_codes || [])
        .map((/** @type {{ code?: string }} */ d) => d.code)
        .filter(Boolean);
    } catch {
      return [];
    }
  }

  /**
   * @param {unknown} button
   * @param {string} label
   */
  #setButtonLabel(button, label) {
    if (!(button instanceof HTMLElement)) return;
    const labelEl = button.querySelector('[data-copy-label]');
    if (labelEl) {
      labelEl.textContent = label;
    } else {
      button.textContent = label;
    }
  }

  /** @param {string} message */
  #announce(message) {
    const live = this.refs.statusMessage;
    if (!(live instanceof HTMLElement)) return;
    live.textContent = message;
  }

  /** @param {string} text */
  #fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
}

if (!customElements.get('welcome-promo')) {
  customElements.define('welcome-promo', WelcomePromo);
}
